<template>
  <div class="h-full d-flex flex-column">
    <v-card-text
      class="pb-0"
      v-if="pages.length > 0"
    >
      <div class="pa-0 d-flex flex-column gap-1">
        <div class="share-title">
          {{ $t('social-media.share.title') }}
        </div>

        <v-list>
          <v-list-item
            v-for="page in pages"
            :key="page.id"
          >
            <div class="sharing-step__item">
              <v-avatar
                tile
                size="30"
              >
                <r-img :src="KindTypeIcon[TypeToKind[page.type]]" />
              </v-avatar>

              <v-skeleton-loader
                v-if="page.loading"
                type="heading"
                width="200"
              />
              <div
                v-else
                class="page-name d-flex flex-column"
              >
                <span>{{ page.name }}</span>
                <span
                  :class="{
                    'text-grey page-disabled-message': true,
                    'page-disabled-message--desktop': !isMobile,
                  }"
                  v-if="typeof page.disabled === 'string'"
                >
                  {{ page.disabled }}
                </span>
              </div>

              <v-switch
                v-model="page.selected"
                :loading="page.loading"
                class="pr-4 ml-4"
                :disabled="typeof page.disabled === 'string'"
                color="primary"
                hide-details
                @update:model-value="selectionChanged"
              />
            </div>
          </v-list-item>
        </v-list>

        <div
          v-if="platformErrors.length"
          class="d-flex flex-column gap-1"
        >
          <v-alert
            v-for="platform in platformErrors"
            :key="platform"
            type="error"
            density="compact"
            tile
            text
            class="mb-0"
          >
            {{ $t('social-media.share.error', { platform: platformLabel(platform) }) }}
          </v-alert>
        </div>
      </div>
    </v-card-text>

    <v-card-actions
      v-if="pages.length > 0"
      :style="{ display: isMobile ? 'block' : '' }"
    >
      <v-spacer />

      <v-tooltip
        location="top"
        :disabled="selectedPages.length > 0"
      >
        <template v-slot:activator="{ props }">
          <div v-bind="props">
            <!-- TODO: add outlined style -->
            <!-- :variant="isMobile ? 'outlined' : undefined" -->
            <v-btn
              color="primary"
              class="continue-button"
              :class="{ 'col-12': isMobile, 'w-100': isMobile }"
              :variant="!isMobile ? 'text' : 'outlined'"
              :disabled="selectedPages.length === 0 || isGenerating"
              :loading="isGenerating"
              data-testid="content-creator-sharing-continue-btn"
              @click="$emit('next')"
            >
              <span>{{ $t('labels.continue') }}</span>
            </v-btn>
          </div>
        </template>
        <span>
          {{ $t('social-media.alert.select-page') }}
        </span>
      </v-tooltip>
    </v-card-actions>

    <v-card-text v-if="!allPagesConnected">
      <v-divider v-if="pages.length > 0" />

      <div class="d-flex align-center gap-2 mt-4 flex-column flex-md-row">
        <div>
          <h3 class="py-1 link-account-title">
            {{ $t('social-media.share.link-account.title') }}
          </h3>
          <div class="link-account-subtitle">
            {{ $t('social-media.share.link-account.subtitle') }}
          </div>
        </div>
        <SocialMediaConnect
          @close="handleConnectClose"
          ref="connect"
        />
      </div>
    </v-card-text>
  </div>
</template>

<script>
import { useSocialMedia } from '@/composables/useSocialMedia'

import KindType, { KindTypeIcon, TypeToKind } from '../enums/KindType'
import { isGoogleBusinessAuthorized, ReadableSocialMedia, SocialMedia } from '../enums/SocialMedia'
import GET_FACEBOOK_PAGES from '../queries/getFacebookPages.gql'
import GET_GOOGLE_BUSINESS_LOCATIONS from '../queries/getGoogleBusinessLocations.gql'
import GET_LINKEDIN_PAGES from '../queries/getLinkedinOrganizations.gql'
import GET_SOCIAL_MEDIA_CONNECTIONS from '../queries/getSocialMediaConnections.gql'
import GOOGLE_BUSINESS_CONNECTION_STATUS from '../queries/googleBusinessConnectionStatus.gql'
import SocialMediaConnect from './connect/SocialMediaConnect.vue'

export default {
  components: { SocialMediaConnect },
  setup() {
    const { canUseGoogleBusiness } = useSocialMedia()
    return { canUseGoogleBusiness }
  },
  props: {
    textKind: {
      type: String,
      required: true,
    },
    selectedPlatforms: {
      type: Array,
      default: () => [],
    },
    isMasterView: {
      type: Boolean,
      default: false,
    },
    isGenerating: {
      type: Boolean,
      default: false,
    },
    canPostToInstagram: {
      type: Boolean,
      default: false,
    },
    isMovVideo: {
      type: Boolean,
      default: false,
    },
  },
  inject: {
    supportCarouselPosts: { default: () => [] },
    selectedCarouselImages: { default: () => [] },
  },
  data() {
    return {
      KindTypeIcon,
      TypeToKind,
      socialMediaConnections: [],
      linkedinPages: [],
      metaPages: [],
      googleBusinessLocations: [],
      googleBusinessStatus: { status: null },
      selectedPages: [],
      platformErrors: [],
      socialMediaConnectionsLoaded: false,
      connectMediaDialog: false,
      pages: [],
    }
  },
  mounted() {
    this.pages = this.getPages()
    this.selectionChanged()
  },
  computed: {
    isMobile() {
      return this.$vuetify.display.smAndDown
    },
    // Master-view channel toggles. Computed rather than static data so `name`
    // comes from the one shared, localised channel-label source.
    platforms() {
      return [
        { id: 'facebook-platform', type: SocialMedia.FACEBOOK },
        { id: 'instagram-platform', type: SocialMedia.INSTAGRAM },
        { id: 'linkedin-platform', type: SocialMedia.LINKEDIN },
        ...(this.canUseGoogleBusiness
          ? [{ id: 'google-business-platform', type: SocialMedia.GOOGLE_BUSINESS }]
          : []),
      ].map(({ id, type }) => ({
        id,
        type,
        name: this.platformLabel(type),
        loading: false,
      }))
    },
    allPagesConnected() {
      // In master view, we don't show connect dialog
      if (this.isMasterView) {
        return true
      }
      const connectedTypes = new Set(this.pages.map(({ type }) => type))
      const requiredTypes = [SocialMedia.FACEBOOK, SocialMedia.INSTAGRAM, SocialMedia.LINKEDIN]
      if (this.canUseGoogleBusiness) {
        requiredTypes.push(SocialMedia.GOOGLE_BUSINESS)
      }
      return requiredTypes.every((type) => connectedTypes.has(type))
    },
    isGoogleBusinessKind() {
      return [KindType.gmbNewsPost, KindType.gmbProductDescription].includes(this.textKind)
    },
    isGoogleBusinessConnected() {
      return isGoogleBusinessAuthorized(this.googleBusinessStatus?.status)
    },
  },
  watch: {
    socialMediaConnectionsLoaded() {
      // Every platform — Google Business included — is connected through this dialog.
      if (!this.isMasterView && (!this.pages || this.pages.length === 0)) {
        this.openConnectMediaDialog()
      }
    },
    metaPages() {
      if (!this.isMasterView) {
        this.pages = this.getPages()
        this.selectionChanged()
      }
    },
    linkedinPages() {
      if (!this.isMasterView) {
        this.pages = this.getPages()
        this.selectionChanged()
      }
    },
    googleBusinessLocations() {
      if (!this.isMasterView) {
        this.pages = this.getPages()
        this.selectionChanged()
      }
    },
  },
  methods: {
    // Single source for every channel name shown in this step.
    platformLabel(type) {
      return ReadableSocialMedia[type]
    },
    // Deduplicated on the SocialMediaType, never on the rendered label — a
    // localised string would make the identity depend on the UI language.
    addPlatformError(type) {
      if (!this.platformErrors?.includes(type)) {
        this.platformErrors.push(type)
      }
    },
    disableKind(platform) {
      if (
        this.selectedCarouselImages.length > 1 &&
        !this.supportCarouselPosts.includes(this.TypeToKind[platform])
      )
        return this.$t('content-creator.does-not-support-carousel-posts')

      if (platform === SocialMedia.INSTAGRAM && !this.canPostToInstagram)
        return this.$t('buttons.content-creator.tooltip.disable-post')

      if (platform === SocialMedia.LINKEDIN && this.isMovVideo)
        return this.$t('content-creator.linkedin-does-not-support-mov')

      return false
    },
    openConnectMediaDialog() {
      this.$refs.connect.open()
    },
    getPages() {
      // For master view, show only platforms without requiring connection
      if (this.isMasterView) {
        const autoSelect = (page) => {
          if (this.selectedCarouselImages.length > 1 && page.type === 'LINKEDIN') return false
          if (this.isMovVideo && page.type === 'LINKEDIN') return false
          if (page.type === SocialMedia.INSTAGRAM && !this.canPostToInstagram) return false
          // Both GBP text kinds (gmbNewsPost, gmbProductDescription) target Google Business,
          // but only gmbNewsPost is in TypeToKind — match the GBP page for either.
          const matchesTextKind =
            this.textKind === TypeToKind[page.type] ||
            (this.isGoogleBusinessKind && page.type === SocialMedia.GOOGLE_BUSINESS)
          return this.selectedPlatforms.length > 0
            ? this.selectedPlatforms.some((type) => type === page.type)
            : this.platforms.length === 1 || matchesTextKind
        }

        return this.platforms.map((page) => ({
          ...page,
          disabled: this.disableKind(page.type),
          selected: autoSelect(page),
        }))
      }

      const pages = [
        ...this.metaPages.map((page) => ({
          ...page,
          type: 'FACEBOOK',
        })),
        ...this.metaPages
          .filter(
            (page) =>
              this.socialMediaConnections?.includes('INSTAGRAM') && page.instagram_business_account
          )
          .map(({ instagram_business_account: { id, username }, loading }) => ({
            id,
            name: username,
            loading,
            type: 'INSTAGRAM',
          })),
        ...this.linkedinPages.map(({ urn, name, loading }) => ({
          id: urn,
          name,
          loading,
          type: 'LINKEDIN',
        })),
        ...this.googleBusinessLocations
          .filter(({ isActive }) => isActive)
          .map(({ id, name, loading }) => ({
            id,
            name,
            loading,
            type: 'GOOGLE_BUSINESS',
          })),
      ]

      const autoSelect = (page) => {
        if (this.selectedCarouselImages.length > 1 && page.type === 'LINKEDIN') return false
        if (this.isMovVideo && page.type === 'LINKEDIN') return false
        // Both GBP text kinds (gmbNewsPost, gmbProductDescription) target Google Business,
        // but only gmbNewsPost is in TypeToKind — match the GBP page for either.
        const matchesTextKind =
          this.textKind === TypeToKind[page.type] ||
          (this.isGoogleBusinessKind && page.type === SocialMedia.GOOGLE_BUSINESS)
        return this.selectedPlatforms.length > 0
          ? this.selectedPlatforms.some((type) => type === page.type)
          : pages.length === 1 || matchesTextKind
      }

      return pages.map((page) => ({
        ...page,
        disabled: this.disableKind(page.type),
        selected: autoSelect(page),
      }))
    },
    selectionChanged() {
      this.selectedPages = this.pages.filter(({ selected }) => selected)
      this.$emit('selectedPages', this.selectedPages)
    },
    // The connect dialog owns the OAuth/location mutations; refetch this component's own
    // queries afterwards so the page list and connection state stay in sync.
    async handleConnectClose() {
      if (!this.isMasterView && this.canUseGoogleBusiness) {
        await this.$apollo.queries.googleBusinessStatus.refetch()
        if (this.isGoogleBusinessConnected) {
          await this.$apollo.queries.googleBusinessLocations.refetch()
        }
      }
      this.pages = this.getPages()
      this.selectionChanged()
    },
  },
  apollo: {
    socialMediaConnections: {
      query: GET_SOCIAL_MEDIA_CONNECTIONS,
      skip() {
        return this.isMasterView
      },
      update({ socialMediaConnections } = { socialMediaConnections: [] }) {
        if (socialMediaConnections?.includes('LINKEDIN') && this.linkedinPages.length === 0) {
          this.linkedinPages = [{ isActive: true, loading: true, id: 'linkedInLoading' }]
        }
        if (socialMediaConnections?.includes('FACEBOOK') && this.metaPages.length === 0) {
          this.metaPages = [{ isActive: true, loading: true, id: 'facebookLoading' }]
        }
        this.socialMediaConnectionsLoaded = true
        return socialMediaConnections
      },
    },
    googleBusinessStatus: {
      query: GOOGLE_BUSINESS_CONNECTION_STATUS,
      skip() {
        return this.isMasterView || !this.canUseGoogleBusiness
      },
      update({ googleBusinessConnectionStatus } = {}) {
        return googleBusinessConnectionStatus || { status: null }
      },
    },
    googleBusinessLocations: {
      query: GET_GOOGLE_BUSINESS_LOCATIONS,
      skip() {
        return this.isMasterView || !this.canUseGoogleBusiness || !this.isGoogleBusinessConnected
      },
      error() {
        this.addPlatformError(SocialMedia.GOOGLE_BUSINESS)
      },
      // Keep the full list (incl. inactive) so the location picker can offer all options;
      // getPages filters to the active location for the shareable page list.
      update(data) {
        return data.googleBusinessLocations
      },
    },
    linkedinPages: {
      query: GET_LINKEDIN_PAGES,
      skip() {
        return this.isMasterView || !this.socialMediaConnections?.includes('LINKEDIN')
      },
      error() {
        this.addPlatformError(SocialMedia.LINKEDIN)
      },
      update(data) {
        return data.linkedinPages.filter(({ isActive }) => isActive)
      },
    },
    metaPages: {
      query: GET_FACEBOOK_PAGES,
      skip() {
        return this.isMasterView || !this.socialMediaConnections?.includes('FACEBOOK')
      },
      error() {
        this.addPlatformError(SocialMedia.FACEBOOK)
      },
      update(data) {
        return data.metaPages.filter(({ isActive }) => isActive)
      },
    },
  },
  emits: ['selectedPages', 'next'],
}
</script>

<style>
.share-title,
.page-name {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
}

.link-account-title {
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 22px;
}

.link-account-subtitle {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
}

.continue-button {
  font-size: 14px !important;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.sharing-step__item {
  display: grid;
  grid-template-columns: 40px 1fr 80px;
  align-items: center;
  gap: 8px;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}
</style>
